// LiveBot.js
import React, { useState, useEffect } from "react";
import { FaInfoCircle } from "react-icons/fa";
import appStyle from "./appStyle.module.css";
import InputDisplay from "./InputDisplay";
import SiteUsers from "./SiteUsers";
import { useTenantPlan } from "../../hooks/useTenantPlan";

// ⭐ Reuse the same confirmation modal that LiveAgentBotSettings
//    uses for "All the clients would be rebooted!" — keeps the
//    settings-page UX consistent.
import Modal from "../Modal";
import modalContentSty from "../SideData/Overview/LeadGenBotSettings/ModalContent.module.css";

// ---------- Default Messages ----------
const DEFAULT_WELCOME_MSG =
  "👋 Hi there! Welcome to our live chat. A support agent will be with you shortly.";
const DEFAULT_OFFLINE_MSG =
  "🕒 We're currently offline. Leave your message and we'll get back to you soon.";

// ---------- Threshold validation ranges ----------
const PENDING_TIMEOUT_MIN = 1;
const PENDING_TIMEOUT_MAX = 30;
const TENANT_LOAD_THRESHOLD_MIN = 1;
const TENANT_LOAD_THRESHOLD_MAX = 50;

// Defaults used when the values aren't yet set on the app data.
// Kept in sync with LIVE_AGENT_DEFAULTS in LiveAgentBotSettings.
const DEFAULT_PENDING_TIMEOUT = 2;
const DEFAULT_TENANT_LOAD_THRESHOLD = 5;

const LiveBot = ({ setBotContent, botContent }) => {
  const [showSiteUsersSection, setShowSiteUsersSection] = useState(false);
  const [infoModal, setInfoModal] = useState({ open: false, title: "", body: null });

  // ⭐ Confirmation modal shown when the admin flips Guest Live
  //    Chat from ON to OFF. Explains that existing guest sessions
  //    persist (until their tab closes) while new visitors will
  //    be anonymous. The pending toggle value is stashed here so
  //    that Cancel restores the previous state and Confirm applies
  //    the new one.
  const [guestToggleConfirm, setGuestToggleConfirm] = useState({
    open: false,
  });

  const { data: tenantPlanData, loading: planLoading } = useTenantPlan();

  const siteUsersLimit = tenantPlanData?.msu_limit || 0;
  const siteUsersRemaining = tenantPlanData?.global_site_users_remaining || 0;
  const totalSiteUsersTopups = tenantPlanData?.total_topups_site_users || 0;
  const isLoadingSiteUsersLimit = planLoading;

  // ---------- Auto-seed default messages on mount (once) ----------
  useEffect(() => {
    if (!botContent) return;

    const hasWelcome =
      botContent.live_settings?.WELCOME_MSG &&
      botContent.live_settings.WELCOME_MSG.trim() !== "";
    const hasOffline =
      botContent.live_settings?.OFFLINE_MSG &&
      botContent.live_settings.OFFLINE_MSG.trim() !== "";

    if (!hasWelcome || !hasOffline) {
      setBotContent((prev) => ({
        ...prev,
        live_settings: {
          ...prev.live_settings,
          WELCOME_MSG: hasWelcome
            ? prev.live_settings.WELCOME_MSG
            : DEFAULT_WELCOME_MSG,
          OFFLINE_MSG: hasOffline
            ? prev.live_settings.OFFLINE_MSG
            : DEFAULT_OFFLINE_MSG,
        },
      }));
    }
  }, []);

  const handleLiveAgentToggle = () => {
    setBotContent((prev) => ({
      ...prev,
      live_agent_enabled: !prev.live_agent_enabled,
    }));
  };

  // ---------- Guest Live Chat Toggle ----------
  //
  // The toggle is asymmetric:
  //
  //   OFF → ON : apply immediately (permissive change, no data risk).
  //   ON  → OFF: open a confirmation modal first. The modal explains
  //              the transition semantics — existing guest sessions
  //              persist until their tab closes, but new visitors
  //              will be anonymous.
  //
  // On Cancel the modal closes and nothing changes. On Confirm we
  // call commitGuestLiveChatToggle() to actually flip the state.
  // ----------
  const handleGuestLiveChatToggle = () => {
    const currentlyOn = botContent?.guest_live_chat_enabled === true;

    if (!currentlyOn) {
      // Off → On: no confirmation needed.
      commitGuestLiveChatToggle();
      return;
    }

    // On → Off: warn first.
    setGuestToggleConfirm({ open: true });
  };

  // Actually flip the flag. Called either directly (Off → On) or
  // from the confirmation modal's Confirm button (On → Off).
  const commitGuestLiveChatToggle = () => {
    setBotContent((prev) => ({
      ...prev,
      guest_live_chat_enabled: !prev.guest_live_chat_enabled,
    }));
    setGuestToggleConfirm({ open: false });
  };

  const cancelGuestLiveChatToggle = () => {
    setGuestToggleConfirm({ open: false });
  };

  // ---------- Video setting toggle ----------
  // Factory that returns a handler for a specific key inside
  // live_settings.video_setting. Using a factory avoids two
  // near-identical handlers (one for audio, one for video).
  const handleVideoSettingToggle = (mediaKey) => () => {
    setBotContent((prev) => ({
      ...prev,
      live_settings: {
        ...prev.live_settings,
        video_setting: {
          audio: prev?.live_settings?.video_setting?.audio ?? true,
          video: prev?.live_settings?.video_setting?.video ?? true,
          [mediaKey]: !(
            prev?.live_settings?.video_setting?.[mediaKey] ?? true
          ),
        },
      },
    }));
  };

  // ---------- Identity storage change ----------
  // Sets the backend for a given identity type. The value is
  // stored as a top-level field on botContent (NOT nested), so
  // it rides on the app data directly. The client SDK reads it
  // at startup.
  //
  // Only "session" and "local" are valid; anything else is
  // ignored so a stray UI value can't poison the app data.
  const handleIdentityStorageChange = (identityType, value) => {
    if (value !== "session" && value !== "local") return;
    setBotContent((prev) => ({
      ...prev,
      identity_storage: {
        visitor: prev?.identity_storage?.visitor ?? "session",
        customer: prev?.identity_storage?.customer ?? "local",
        [identityType]: value,
      },
    }));
  };

  // ---------- Numeric field helper for Queue Thresholds ----------
  // Clamps to [min, max] and stores the integer on botContent
  // directly (NOT nested — these are top-level app-data fields).
  const handleNumericFieldChange = (key, rawValue, min, max) => {
    if (rawValue === "") return;
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return;
    const clamped = Math.min(Math.max(Math.floor(parsed), min), max);
    setBotContent((prev) => ({ ...prev, [key]: clamped }));
  };

  const updateLiveSetting = (key, value) => {
    setBotContent((prev) => ({
      ...prev,
      live_settings: {
        ...prev.live_settings,
        [key]: value,
      },
    }));
  };

  // ---------- Info Modal content ----------
  const openInfo = (which) => {
    if (which === "welcome") {
      setInfoModal({
        open: true,
        title: "Welcome Message",
        body: (
          <>
            <p>
              This is the <strong>first message</strong> a visitor sees when they open
              the live chat — <em>while your agents are online</em>.
            </p>
            <ul>
              <li>Keep it warm, short, and reassuring.</li>
              <li>Mention that a human agent is joining shortly.</li>
              <li>Max <strong>50 words</strong> is recommended for best UX.</li>
            </ul>
            <p style={{ marginTop: "10px", color: "#64748b" }}>
              💡 We've pre-filled a friendly default. You can edit it anytime —
              just hit <strong>Save</strong> after making changes.
            </p>
          </>
        ),
      });
    } else if (which === "guest") {
      setInfoModal({
        open: true,
        title: "Guest Live Chat",
        body: (
          <>
            <p>
              Controls whether <strong>unauthenticated visitors</strong> are allowed
              to start a live chat with your support agents.
            </p>
            <ul>
              <li>
                <strong>Enabled</strong> — Guests can initiate a chat without logging
                in. Best for public marketing sites and open support.
              </li>
              <li>
                <strong>Disabled</strong> — Only authenticated users can start a live
                chat. Best for logged-in portals and internal tools.
              </li>
            </ul>
            <p style={{ marginTop: "10px", color: "#64748b" }}>
              💡 Existing visitors keep their guest session until their browser
              tab closes. New visitors will be anonymous.
            </p>
          </>
        ),
      });
    } else if (which === "video") {
      setInfoModal({
        open: true,
        title: "Video Call Media",
        body: (
          <>
            <p>
              Controls which media streams the chat widget requests when a
              visitor starts a video call with a live agent.
            </p>
            <ul>
              <li>
                <strong>Camera</strong> — whether the widget asks for the
                visitor's video. Off means calls start audio-only.
              </li>
              <li>
                <strong>Microphone</strong> — whether the widget asks for the
                visitor's audio. Off means calls start video-only.
              </li>
            </ul>
            <p style={{ marginTop: "10px", color: "#64748b" }}>
              💡 Turning <strong>both</strong> off effectively disables video
              calls. Visitors will still see the live-agent chat — they just
              won't be able to start a call.
            </p>
            <p style={{ marginTop: "10px", color: "#64748b" }}>
              These settings are read by the chat widget at call time.
            </p>
          </>
        ),
      });
    } else if (which === "storage") {
      setInfoModal({
        open: true,
        title: "Session Persistence",
        body: (
          <>
            <p>
              Where the chat widget stores each type of identity in the
              visitor's browser.
            </p>
            <ul>
              <li>
                <strong>Visitor session</strong> — the identity used for
                guests who have not signed in. Default: <em>Session only</em>,
                which means the visitor's guest session ends when they close
                the tab.
              </li>
              <li>
                <strong>Customer session</strong> — the identity used for
                users who have signed in. Default: <em>Persistent</em>, which
                means they stay logged in across browser restarts.
              </li>
            </ul>
            <p style={{ marginTop: "10px", color: "#64748b" }}>
              💡 Choosing <strong>Session only</strong> for customers logs
              every user out when they close the tab. Best for
              high-security or shared-computer scenarios.
            </p>
            <p style={{ marginTop: "10px", color: "#64748b" }}>
              These settings are read once at widget load time. Existing
              sessions are not moved — a change takes effect on the next
              page load after the current session ends.
            </p>
          </>
        ),
      });
    } else {
      setInfoModal({
        open: true,
        title: "Offline Message",
        body: (
          <>
            <p>
              This message is shown when <strong>no agent is online</strong> —
              for example, outside business hours or during peak load.
            </p>
            <ul>
              <li>Let visitors know you're away, not ignoring them.</li>
              <li>Invite them to leave their message or contact info.</li>
              <li>Max <strong>30 words</strong> is recommended.</li>
            </ul>
            <p style={{ marginTop: "10px", color: "#64748b" }}>
              💡 We've pre-filled a helpful default. Edit and hit{" "}
              <strong>Save</strong> to customize.
            </p>
          </>
        ),
      });
    }
  };

  return (
    <div className={appStyle.wrap_welcome_message}>
      {/* Live Agent Enabled Toggle */}
      <div className={appStyle.controlGroup}>
        <label className={appStyle.controlLabel}>
          <div style={{ textAlign: "left" }}>
            <span className={appStyle.labelText}>Live Agent Features</span>
            <p className={appStyle.helperText}>
              {botContent.live_agent_enabled
                ? "Live agent features are enabled. Users can interact with live agents."
                : "Live agent features are disabled. All live agent settings below are hidden."}
            </p>
          </div>
          <div className={appStyle.toggleContainer}>
            <div className={appStyle.liveAgentToggle}>
              <span className={appStyle.toggleLabel}>
                {botContent.live_agent_enabled ? "Enabled" : "Disabled"}
              </span>
              <div
                className={`${appStyle.toggleSwitch} ${
                  botContent.live_agent_enabled
                    ? appStyle.toggleOn
                    : appStyle.toggleOff
                }`}
                onClick={handleLiveAgentToggle}
              >
                <div className={appStyle.toggleSlider}></div>
              </div>
            </div>
          </div>
        </label>
      </div>

      {botContent.live_agent_enabled && (
        <>
          {/* Site Users & Limits Dropdown */}
          <div className={appStyle.dropdownSection}>
            <div
              className={appStyle.dropdownHeader}
              onClick={() => setShowSiteUsersSection(!showSiteUsersSection)}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                📊 Site Users & Limits
              </h3>
              <span
                style={{
                  fontSize: "20px",
                  color: "#007bff",
                  transform: showSiteUsersSection
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              >
                ▼
              </span>
            </div>

            {showSiteUsersSection && (
              <div className={appStyle.dropdownContent}>
                <SiteUsers
                  limit={siteUsersLimit}
                  remaining={siteUsersRemaining}
                  totalTopups={totalSiteUsersTopups}
                  isLoading={isLoadingSiteUsersLimit}
                />
              </div>
            )}
          </div>

          <h3>Live User Settings</h3>

          {/* ============================================================
              Guest Live Chat Toggle
              ============================================================
              Controls whether unauthenticated visitors can start a
              live chat. Read by the chat widget at load time.
              ============================================================ */}
          <div className={appStyle.controlGroup} data-tour="enable-guest-live-chat">
            <label className={appStyle.controlLabel}>
              <div style={{ textAlign: "left" }}>
                <span className={appStyle.labelText}>
                  Guest Live Chat
                  <FaInfoCircle
                    size={14}
                    style={{
                      marginLeft: "8px",
                      cursor: "pointer",
                      color: "#64748b",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openInfo("guest");
                    }}
                  />
                </span>
                <p className={appStyle.helperText}>
                  {botContent.guest_live_chat_enabled
                    ? "Guests (unauthenticated visitors) can start a live chat."
                    : "Only authenticated users can start a live chat."}
                </p>

                {/* ⭐ Transition note. Shows permanently under the
                    toggle whenever it's OFF, so an admin who saves
                    without seeing the confirmation modal (e.g. they
                    enabled the flag last week and disabled it today
                    via a different tab) still sees what the current
                    state means for in-flight guest sessions. */}
                {!botContent.guest_live_chat_enabled && (
                  <p
                    className={appStyle.helperText}
                    style={{
                      marginTop: "6px",
                      fontSize: "12px",
                      color: "#64748b",
                      lineHeight: "1.5",
                    }}
                  >
                    ℹ️ Existing visitors who already have a guest session
                    keep it until their browser tab closes. New visitors
                    will be anonymous until you re-enable this.
                  </p>
                )}
              </div>
              <div className={appStyle.toggleContainer}>
                <div className={appStyle.liveAgentToggle}>
                  <span className={appStyle.toggleLabel}>
                    {botContent.guest_live_chat_enabled ? "Enabled" : "Disabled"}
                  </span>
                  <div
                    className={`${appStyle.toggleSwitch} ${
                      botContent.guest_live_chat_enabled
                        ? appStyle.toggleOn
                        : appStyle.toggleOff
                    }`}
                    onClick={handleGuestLiveChatToggle}
                  >
                    <div className={appStyle.toggleSlider}></div>
                  </div>
                </div>
              </div>
            </label>
          </div>
          {/* ============================================================
              END Guest Live Chat Toggle
              ============================================================ */}

          {/* ============================================================
              Session Persistence
              ============================================================
              Where each identity type is stored in the browser.

              • Visitor (guest uid) — default sessionStorage.
              • Customer (real uid) — default localStorage.

              Storage backend determines how long a user stays
              logged in:
                • sessionStorage → cleared when the browser tab closes.
                • localStorage   → persists across restarts until the
                                   user logs out or clears their browser
                                   storage.

              The client SDK reads these at startup from
              tezkit_app_data.identity_storage.
              ============================================================ */}
          <div
            className={appStyle.controlGroup}
            data-tour="enable-session-persistence"
          >
            <label className={appStyle.controlLabel}>
              <div style={{ textAlign: "left" }}>
                <span className={appStyle.labelText}>
                  Session Persistence
                  <FaInfoCircle
                    size={14}
                    style={{
                      marginLeft: "8px",
                      cursor: "pointer",
                      color: "#64748b",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openInfo("storage");
                    }}
                  />
                </span>
                <p className={appStyle.helperText}>
                  Choose how long each identity type stays logged in
                  on a visitor's browser.
                </p>
              </div>
            </label>

            {/* ── Visitor storage ── */}
            <div
              className={appStyle.controlGroup}
              style={{ marginTop: "12px", marginBottom: "8px" }}
            >
              <label className={appStyle.controlLabel}>
                <div style={{ textAlign: "left" }}>
                  <span className={appStyle.labelText}>
                    Visitor session
                  </span>
                  <p className={appStyle.helperText}>
                    Applies to guests who are not signed in.
                  </p>
                </div>
                <div className={appStyle.toggleContainer}>
                  <select
                    value={
                      botContent?.identity_storage?.visitor || "session"
                    }
                    onChange={(e) =>
                      handleIdentityStorageChange("visitor", e.target.value)
                    }
                    style={{
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                      background: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <option value="session">
                      Session only (clears on tab close)
                    </option>
                    <option value="local">
                      Persistent (survives restarts)
                    </option>
                  </select>
                </div>
              </label>
            </div>

            {/* ── Customer storage ── */}
            <div
              className={appStyle.controlGroup}
              style={{ marginBottom: "8px" }}
            >
              <label className={appStyle.controlLabel}>
                <div style={{ textAlign: "left" }}>
                  <span className={appStyle.labelText}>
                    Customer session
                  </span>
                  <p className={appStyle.helperText}>
                    Applies to users who have signed in.
                  </p>
                </div>
                <div className={appStyle.toggleContainer}>
                  <select
                    value={
                      botContent?.identity_storage?.customer || "local"
                    }
                    onChange={(e) =>
                      handleIdentityStorageChange("customer", e.target.value)
                    }
                    style={{
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                      background: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <option value="session">
                      Session only (clears on tab close)
                    </option>
                    <option value="local">
                      Persistent (survives restarts)
                    </option>
                  </select>
                </div>
              </label>
            </div>

            {/* ── Warning when customer = session ── */}
            {botContent?.identity_storage?.customer === "session" && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "10px 12px",
                  background: "#fff7ed",
                  border: "1px solid #fdba74",
                  borderRadius: "6px",
                  fontSize: "13px",
                  color: "#9a3412",
                  lineHeight: "1.5",
                }}
              >
                ⚠️ With customer session set to "Session only", signed-in
                users will be logged out every time they close their
                browser tab. Best for high-security apps.
              </div>
            )}
          </div>
          {/* ============================================================
              END Session Persistence
              ============================================================ */}

          {/* ============================================================
              Video Call Settings
              ============================================================
              Controls what media the client SDK asks for when a video
              call starts. Both flags default to true (camera + mic),
              matching the pre-flag behavior.

              Semantics:
                • video: true  → camera stream requested
                • audio: true  → microphone stream requested
                • both false   → video call button should be hidden by
                                 the client (falls back to text-only)

              The client SDK reads these at call time from
              tezkit_app_data.live_settings.video_setting.
              ============================================================ */}
          <div className={appStyle.controlGroup}>
            <label className={appStyle.controlLabel}>
              <div style={{ textAlign: "left" }}>
                <span className={appStyle.labelText}>
                  Video Call Media
                  <FaInfoCircle
                    size={14}
                    style={{
                      marginLeft: "8px",
                      cursor: "pointer",
                      color: "#64748b",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openInfo("video");
                    }}
                  />
                </span>
                <p className={appStyle.helperText}>
                  Choose which media streams the widget requests when a
                  visitor starts a video call. Turn both off to disable
                  video calls entirely.
                </p>
              </div>
            </label>

            {/* ── Camera toggle ── */}
            <div
              className={appStyle.controlGroup}
              style={{ marginTop: "12px", marginBottom: "8px" }}
            >
              <label className={appStyle.controlLabel}>
                <div style={{ textAlign: "left" }}>
                  <span className={appStyle.labelText}>Camera</span>
                  <p className={appStyle.helperText}>
                    {botContent?.live_settings?.video_setting?.video
                      ? "Visitors can share their camera."
                      : "Camera is disabled — calls start audio-only."}
                  </p>
                </div>
                <div className={appStyle.toggleContainer}>
                  <div className={appStyle.liveAgentToggle}>
                    <span className={appStyle.toggleLabel}>
                      {botContent?.live_settings?.video_setting?.video
                        ? "Enabled"
                        : "Disabled"}
                    </span>
                    <div
                      className={`${appStyle.toggleSwitch} ${
                        botContent?.live_settings?.video_setting?.video
                          ? appStyle.toggleOn
                          : appStyle.toggleOff
                      }`}
                      onClick={handleVideoSettingToggle("video")}
                    >
                      <div className={appStyle.toggleSlider}></div>
                    </div>
                  </div>
                </div>
              </label>
            </div>

            {/* ── Microphone toggle ── */}
            <div
              className={appStyle.controlGroup}
              style={{ marginBottom: "8px" }}
            >
              <label className={appStyle.controlLabel}>
                <div style={{ textAlign: "left" }}>
                  <span className={appStyle.labelText}>Microphone</span>
                  <p className={appStyle.helperText}>
                    {botContent?.live_settings?.video_setting?.audio
                      ? "Visitors can share their microphone."
                      : "Microphone is disabled — calls start video-only."}
                  </p>
                </div>
                <div className={appStyle.toggleContainer}>
                  <div className={appStyle.liveAgentToggle}>
                    <span className={appStyle.toggleLabel}>
                      {botContent?.live_settings?.video_setting?.audio
                        ? "Enabled"
                        : "Disabled"}
                    </span>
                    <div
                      className={`${appStyle.toggleSwitch} ${
                        botContent?.live_settings?.video_setting?.audio
                          ? appStyle.toggleOn
                          : appStyle.toggleOff
                      }`}
                      onClick={handleVideoSettingToggle("audio")}
                    >
                      <div className={appStyle.toggleSlider}></div>
                    </div>
                  </div>
                </div>
              </label>
            </div>

            {/* ── Warning when both are off ── */}
            {botContent?.live_settings?.video_setting?.video === false &&
              botContent?.live_settings?.video_setting?.audio === false && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "10px 12px",
                    background: "#fff7ed",
                    border: "1px solid #fdba74",
                    borderRadius: "6px",
                    fontSize: "13px",
                    color: "#9a3412",
                    lineHeight: "1.5",
                  }}
                >
                  ⚠️ With camera and microphone both off, video calls
                  are effectively disabled. Visitors will only be able
                  to use text chat with a live agent.
                </div>
              )}
          </div>
          {/* ============================================================
              END Video Call Settings
              ============================================================ */}

          {/* Session Management – Coming Soon */}
          <div className={appStyle.controlGroup}>
            <label className={appStyle.controlLabel}>
              <div>
                <span className={appStyle.labelText}>
                  Session Management
                  <span className={appStyle.comingSoonBadge}>Coming Soon</span>
                </span>
                <p className={appStyle.helperText}>
                  Session Management is coming soon.
                </p>
              </div>
              <div className={appStyle.toggleContainer}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <span className={appStyle.toggleLabel}>Disabled</span>
                  <div
                    className={`${appStyle.toggleSwitch} ${appStyle.toggleOff}`}
                    style={{
                      opacity: 0.5,
                      cursor: "not-allowed",
                      pointerEvents: "none",
                    }}
                    title="Coming Soon"
                  >
                    <div className={appStyle.toggleSlider}></div>
                  </div>
                </div>
              </div>
            </label>
          </div>

          {/* ============================================================
              Queue Thresholds
              ============================================================
              Admin-configurable client behavior while waiting for a
              live agent to accept a join request. Read by the client
              SDK at load time.

                • Pending timeout: how long the client waits before
                  checking tenant load.

                • Tenant load threshold: total pending + connected
                  sessions at which the client gives up and shows
                  "all agents are busy."
              ============================================================ */}
          <div
            style={{
              marginTop: "32px",
              padding: "20px",
              border: "1px solid #e5e5e5",
              borderRadius: "8px",
              background: "#fafafa",
            }}
          >
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#333",
              }}
            >
              Queue Thresholds
            </h3>
            <p
              style={{
                margin: "0 0 20px 0",
                fontSize: "13px",
                color: "#666",
                lineHeight: "1.5",
              }}
            >
              Control how long a client waits for a live agent and when
              they should be told that all agents are busy. These
              values are read by the chat widget at load time.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
              }}
            >
              {/* Pending timeout */}
              <div>
                <label
                  htmlFor="pending_timeout_in_minute"
                  style={{
                    display: "block",
                    fontWeight: "600",
                    marginBottom: "6px",
                    fontSize: "14px",
                    color: "#333",
                  }}
                >
                  Pending Timeout (minutes)
                </label>
                <input
                  id="pending_timeout_in_minute"
                  type="number"
                  min={PENDING_TIMEOUT_MIN}
                  max={PENDING_TIMEOUT_MAX}
                  step="1"
                  value={botContent.pending_timeout_in_minute ?? ""}
                  onChange={(e) =>
                    handleNumericFieldChange(
                      "pending_timeout_in_minute",
                      e.target.value,
                      PENDING_TIMEOUT_MIN,
                      PENDING_TIMEOUT_MAX,
                    )
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    background: "#fff",
                  }}
                />
                <p
                  style={{
                    margin: "6px 0 0 0",
                    fontSize: "12px",
                    color: "#888",
                    lineHeight: "1.4",
                  }}
                >
                  How long the client waits after sending a join request
                  before checking the tenant&apos;s overall agent load.
                  Default: {DEFAULT_PENDING_TIMEOUT} minutes. Range:{" "}
                  {PENDING_TIMEOUT_MIN}–{PENDING_TIMEOUT_MAX}.
                </p>
              </div>

              {/* Tenant load threshold */}
              <div>
                <label
                  htmlFor="tenant_load_threshold"
                  style={{
                    display: "block",
                    fontWeight: "600",
                    marginBottom: "6px",
                    fontSize: "14px",
                    color: "#333",
                  }}
                >
                  Tenant Load Threshold
                </label>
                <input
                  id="tenant_load_threshold"
                  type="number"
                  min={TENANT_LOAD_THRESHOLD_MIN}
                  max={TENANT_LOAD_THRESHOLD_MAX}
                  step="1"
                  value={botContent.tenant_load_threshold ?? ""}
                  onChange={(e) =>
                    handleNumericFieldChange(
                      "tenant_load_threshold",
                      e.target.value,
                      TENANT_LOAD_THRESHOLD_MIN,
                      TENANT_LOAD_THRESHOLD_MAX,
                    )
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    background: "#fff",
                  }}
                />
                <p
                  style={{
                    margin: "6px 0 0 0",
                    fontSize: "12px",
                    color: "#888",
                    lineHeight: "1.4",
                  }}
                >
                  Total pending + connected sessions at which a waiting
                  client gives up and sees &quot;all agents are busy.&quot;
                  If the current load is below this number, the client
                  keeps waiting. Default: {DEFAULT_TENANT_LOAD_THRESHOLD}.
                  Range: {TENANT_LOAD_THRESHOLD_MIN}–
                  {TENANT_LOAD_THRESHOLD_MAX}.
                </p>
              </div>
            </div>
          </div>
          {/* ============================================================
              END Queue Thresholds
              ============================================================ */}

          {/* Welcome & Offline Messages */}
          <div className={appStyle.welcome_message}>
            <InputDisplay
              placeholder="Type a Welcome Message for users to see..."
              handleSave={(val) => updateLiveSetting("WELCOME_MSG", val)}
              maxWords={50}
              initialValue={botContent.live_settings?.WELCOME_MSG || ""}
              defaultValue={DEFAULT_WELCOME_MSG}
              onInfoClick={() => openInfo("welcome")}
            />

            <InputDisplay
              placeholder="Type an Offline Message..."
              handleSave={(val) => updateLiveSetting("OFFLINE_MSG", val)}
              maxWords={30}
              initialValue={botContent.live_settings?.OFFLINE_MSG || ""}
              defaultValue={DEFAULT_OFFLINE_MSG}
              onInfoClick={() => openInfo("offline")}
            />
          </div>
        </>
      )}

      {!botContent.live_agent_enabled && (
        <div className={appStyle.disabledMessage}>
          <p>
            Live agent features are currently disabled. Enable the toggle above
            to configure live user settings.
          </p>
        </div>
      )}

      {/* Info Modal */}
      {infoModal.open && (
        <div
          className={appStyle.modalOverlay}
          onClick={() => setInfoModal({ open: false, title: "", body: null })}
        >
          <div
            className={appStyle.modal}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={appStyle.closeButton}
              onClick={() =>
                setInfoModal({ open: false, title: "", body: null })
              }
            >
              ✕
            </button>
            <div className={appStyle.modalContent}>
              <h3 style={{ marginTop: 0, marginBottom: "12px" }}>
                {infoModal.title}
              </h3>
              <div style={{ fontSize: "14px", lineHeight: "1.7", color: "#334155" }}>
                {infoModal.body}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ⭐ Guest Live Chat disable-confirmation modal.
          Opens only when the admin flips the toggle ON → OFF.
          Cancel restores the previous state; Confirm applies. */}
      <Modal
        isOpen={guestToggleConfirm.open}
        onClose={cancelGuestLiveChatToggle}
        hideCloseButton={true}
        title="Disable Guest Live Chat?"
      >
        <div className={modalContentSty.modalContent}>
          <div className={modalContentSty.modalContentWrap}>
            <div className={modalContentSty.modalContentIc}>⚠️</div>
            <p className={modalContentSty.modalMessage}>
              New visitors will no longer be able to chat with a
              live agent without signing in.
            </p>
          </div>

          <div
            style={{
              textAlign: "left",
              fontSize: "13px",
              color: "#475569",
              lineHeight: "1.6",
              padding: "0 8px 8px",
            }}
          >
            <p style={{ margin: "0 0 10px 0" }}>
              <strong>What changes immediately:</strong>
            </p>
            <ul style={{ margin: "0 0 12px 0", paddingLeft: "18px" }}>
              <li>
                Any visitor who has never opened this chat will not be
                given a guest identity.
              </li>
              <li>
                They will still be able to use the AI assistant and FAQ
                (if enabled), but live-agent chat will ask them to sign
                in first.
              </li>
            </ul>

            <p style={{ margin: "0 0 10px 0" }}>
              <strong>What happens to existing guests:</strong>
            </p>
            <ul style={{ margin: "0 0 12px 0", paddingLeft: "18px" }}>
              <li>
                Visitors who already have a guest session on their
                browser will keep it until their tab closes or they
                clear their browser storage.
              </li>
              <li>
                We do not revoke their identity mid-session — doing so
                could interrupt in-flight conversations.
              </li>
            </ul>

            <p style={{ margin: 0, color: "#64748b" }}>
              💡 To force the change for everyone right now, you would
              need to ask those visitors to clear their browser
              storage. We don't do that automatically.
            </p>
          </div>

          <div className={modalContentSty.modalActions}>
            <button
              className={`${modalContentSty.modalButton} ${modalContentSty.cancelBtn}`}
              onClick={cancelGuestLiveChatToggle}
            >
              Cancel
            </button>
            <button
              className={`${modalContentSty.modalButton} ${modalContentSty.okBtn}`}
              onClick={commitGuestLiveChatToggle}
            >
              Disable
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LiveBot;